/**
 * CardBlock - Simplified card component that replaces CommonCard
 * Easy to use with minimal props for content editing
 */

import React from 'react'
import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import MainCard from '../../MainCard/MainCard'
import Heading from '../elements/Heading'
import Image from '../elements/Image'

interface CardBlockProps {
	title: string
	content?: string
	listItems?: string[]
	icon?: string
	image?: string
	backgroundColor?: 'default' | 'white' | 'transparent'
	hover?: boolean
	padding?: 'small' | 'medium' | 'large'
}

const CardBlock: React.FC<CardBlockProps> = ({
	title,
	content,
	listItems,
	icon,
	image,
	backgroundColor = 'default',
	hover = true,
	padding = 'medium'
}) => {
	const theme = useTheme()

	const getPadding = () => {
		switch (padding) {
			case 'small':
				return 2
			case 'medium':
				return 3
			case 'large':
				return 4
			default:
				return 3
		}
	}

	const getBackgroundColor = () => {
		switch (backgroundColor) {
			case 'white':
				return theme.palette.common.white
			case 'transparent':
				return 'transparent'
			case 'default':
				return theme.palette.mode === 'dark' ? theme.palette.custom?.main : theme.palette.grey[50]
			default:
				return theme.palette.mode === 'dark' ? theme.palette.custom?.main : theme.palette.grey[50]
		}
	}

	return (
		<MainCard
			contentSX={{ p: getPadding() }}
			sx={{
				height: '100%',
				backgroundColor: getBackgroundColor(),
				cursor: hover ? 'pointer' : 'default',
				...(hover && {
					'&:hover': {
						bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey.A700 : theme.palette.common.white
					}
				})
			}}
		>
			<Box sx={{ textAlign: 'start' }}>
				{icon && (
					<Box sx={{ mb: 2 }}>
						<Image src={icon} alt={`${title} icon`} width={48} height={48} />
					</Box>
				)}

				<Heading level={3} color="inherit" weight="bold">
					{title}
				</Heading>

				{(content || listItems) && (
					<Box sx={{ mt: 2 }}>
						<Typography
							variant="body1"
							component="div"
							sx={{
								color: theme.palette.mode === 'dark' ? theme.palette.custom?.light : 'black'
							}}
						>
							{content ? (
								content
							) : (
								<ul style={{ margin: 0, paddingLeft: 0 }}>
									{listItems?.map((_content, index) => (
										<li
											key={index}
											style={{
												listStyleType: 'none',
												marginBottom: 0
											}}
										>
											{_content}
										</li>
									))}
								</ul>
							)}
						</Typography>
					</Box>
				)}

				{image && (
					<Box sx={{ mt: 2 }}>
						<Image src={image} alt={title} width="100%" height="auto" />
					</Box>
				)}
			</Box>
		</MainCard>
	)
}

export default CardBlock
